import { del, get, post, put } from 'services/request';
import { IItemId } from 'types/content';
import { IRefIds } from '../constants/refs';
import { BaseModel, IModelCourseOptions, IModuleSearch } from './BaseModel';

export type IActiveInflectTemplate = IItemId & {
  name: string;
  wordClass: number;
  effectiveWordClass: number;
  wordClassName: string;
  activeInflectsCount: number;
  panelsCount: number;
  createdAt: string;
  updatedAt: string;
};

type IActiveInflectTemplateEdit = IItemId & {
  name: string;
  wordClass: number;
  panels?: number[];
  deletable?: boolean;
};

export default class ActiveInflectTemplatesModel extends BaseModel<IActiveInflectTemplate, IActiveInflectTemplateEdit> {
  readonly DIFFERENT_NEW_ID = -1;
  onSave?: (templateId: number) => Promise<void>;

  constructor(options: IModelCourseOptions) {
    super(options);
    this.ref = IRefIds.activeInflectTemplates;
    this.gridName = 'ActiveInflectTemplates';
    this.getGridColumns();
  }

  async getGridSearch(text: string) {
    await super.getGrid();
    this.render();

    this.gridData = await get<IActiveInflectTemplate[]>(
      `/courses/${this.courseId}/active-inflect-templates?text=${text}`,
    );

    this.loadingGrid = false;
    this.render();
  }

  getGridData() {
    return get<IActiveInflectTemplate[]>(`courses/${this.courseId}/active-inflect-templates`);
  }

  async getGrid() {
    await super.getGrid();
    this.gridData = await this.getGridData();
    this.loadingGrid = false;

    this.render();
  }

  async getGridRow(itemId: number) {
    return await get<IActiveInflectTemplate>(`courses/${this.courseId}/active-inflect-templates/${itemId}`);
  }

  async reload() {
    await super.reload();
    await this.getGrid();
  }

  async new() {
    await super.new();
    const item = await get<IActiveInflectTemplateEdit>(`active-inflect-templates/new`);
    await this.setItem(item);
    this.render();
  }

  async save() {
    await super.save();
    const { id } = this.itemUpdated;
    if (id === this.DIFFERENT_NEW_ID) {
      await this.createItem();
    } else {
      await this.updateItem();
    }
  }

  async updateItem() {
    await put(`active-inflect-templates/${this.itemId}`, this.itemUpdated);

    // Call onSave callback if it exists (to save active inflects)
    if (this.onSave) {
      await this.onSave(this.itemId);
    }

    await Promise.all([this.updateGridRow(this.itemId, 'update'), this.loadItemEdit(this.itemId)]);
  }

  async createItem() {
    const result = await post<IItemId>(`courses/${this.courseId}/active-inflect-templates`, {
      ...this.itemUpdated,
      id: this.NEW_CREATE_ID,
    });

    console.log('createItem result', result);

    if (result.ok && result.data) {
      const newItemId = result.data.id;
      this.itemId = newItemId;

      // Call onSave callback if it exists (to save active inflects)
      if (this.onSave) {
        await this.onSave(newItemId);
      }

      await Promise.all([this.updateGridRow(newItemId, 'add'), this.loadItemEdit(newItemId)]);
    }
  }

  async delete() {
    const { id } = this.itemUpdated;

    const result = await del(`active-inflect-templates/${id}`);

    if (result.ok) {
      await Promise.all([this.updateGridRow(id, 'remove'), this.new()]);
    } else {
      console.error(JSON.stringify(result.data));
    }
  }

  async loadItemData(itemId: number) {
    return await get<IActiveInflectTemplateEdit>(`active-inflect-templates/${itemId}`);
  }

  async search(data: IModuleSearch) {
    await this.getGridSearch(data.text ?? '');
  }
}
